import { useState, useMemo } from 'react';
import { docTitle } from '../../domain/documentTitle';
import { api } from '../../api';
import { formatSize, formatDate } from '../../hooks/useInventoryData';
import useHelpTarget from '../../hooks/useHelpTarget';

/**
 * DuplicateGroupPanel — shows documents grouped by content hash when
 * the duplicate filter is active. For each group, lets the user keep
 * the oldest/newest copy and delete the rest.
 *
 * Props:
 *   documents    — full document array
 *   hashCountMap — { hash: count } from useInventoryData
 *   caseId       — UUID
 *   onDocChange  — callback() after mutation
 */
export default function DuplicateGroupPanel({ documents, hashCountMap, caseId, onDocChange, filterHash }) {
  const helpRef = useHelpTarget({
    slug: 'finding-and-removing-duplicates',
    label: 'Duplicate group panel',
    question: 'How does Chargestack decide what is a duplicate, and which copy should I keep?',
  });
  const [busy, setBusy] = useState(null); // hash being processed
  const [status, setStatus] = useState(null);

  const groups = useMemo(() => {
    const byHash = {};
    for (const d of documents) {
      const h = d.file_hash_sha256;
      if (!h || !hashCountMap[h] || hashCountMap[h] < 2) continue;
      if (filterHash && h !== filterHash) continue;
      if (!byHash[h]) byHash[h] = [];
      byHash[h].push(d);
    }
    // Sort each group by created_at ascending (oldest first)
    for (const h of Object.keys(byHash)) {
      byHash[h].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    }
    return Object.entries(byHash).sort((a, b) => b[1].length - a[1].length);
  }, [documents, hashCountMap]);

  async function handleKeep(hash, mode) {
    const group = groups.find(([h]) => h === hash);
    if (!group) return;
    const docs = group[1];
    const sorted = [...docs].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    const keeper = mode === 'oldest' ? sorted[0] : sorted[sorted.length - 1];
    const toDelete = sorted.filter(d => d.id !== keeper.id).map(d => d.id);

    if (toDelete.length === 0) return;
    setBusy(hash);
    setStatus(null);
    try {
      await api.post(`/documents/case/${caseId}/bulk-delete`, {
        document_ids: toDelete,
      });
      setStatus({ text: `Kept "${keeper.filename}", removed ${toDelete.length} duplicate(s)`, error: false });
      onDocChange?.();
    } catch (err) {
      setStatus({ text: `Delete failed: ${err.message}`, error: true });
    } finally {
      setBusy(null);
    }
  }

  if (groups.length === 0) {
    return (
      <div className="dup-panel-empty">
        No duplicate documents found.
      </div>
    );
  }

  return (
    <div className="dup-panel" ref={helpRef}>
      <div className="dup-panel-header">
        <span className="dup-panel-title">
          🔁 {groups.length} duplicate group{groups.length !== 1 ? 's' : ''}
        </span>
        <span className="dup-panel-subtitle">
          Documents with identical content (SHA-256 hash match)
        </span>
      </div>

      {status && (
        <div className={`dup-panel-status ${status.error ? 'dup-panel-status--error' : 'dup-panel-status--ok'}`}>
          {status.text}
        </div>
      )}

      {groups.map(([hash, docs]) => (
        <div key={hash} className="dup-group">
          <div className="dup-group-header">
            <span className="dup-group-count">{docs.length} copies</span>
            <span className="dup-group-hash" title={hash}>
              SHA-256: {hash.substring(0, 12)}…
            </span>
            <span className="dup-group-size">{formatSize(docs[0]?.file_size)}</span>
          </div>
          <div className="dup-group-files">
            {docs.map((d, i) => (
              <div key={d.id} className="dup-group-file">
                <span className="dup-group-file-num">
                  {i + 1}
                </span>
                <span className="dup-group-file-name" title={d.filename}>
                  {docTitle(d)}
                </span>
                <span className="dup-group-file-date">
                  {formatDate(d.created_at)}
                </span>
                <span className={`dup-group-file-tag${i === 0 ? ' dup-tag-oldest' : i === docs.length - 1 ? ' dup-tag-newest' : ''}`}>
                  {i === 0 ? 'ORIGINAL' : i === docs.length - 1 ? 'LATEST' : `copy ${i}`}
                </span>
              </div>
            ))}
          </div>
          <div className="dup-group-actions">
            <button
              className="btn btn-sm dup-keep-btn"
              disabled={busy === hash}
              onClick={() => handleKeep(hash, 'oldest')}
              title="Keep the oldest copy and delete all others"
            >
              Keep Oldest
            </button>
            <button
              className="btn btn-sm dup-keep-btn"
              disabled={busy === hash}
              onClick={() => handleKeep(hash, 'newest')}
              title="Keep the newest copy and delete all others"
            >
              Keep Newest
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
